import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, FileSearch, Globe, Code, BarChart3, ArrowLeft, 
  RefreshCw, Copy, Download, CheckCircle, XCircle, AlertTriangle,
  TrendingUp, Eye, MousePointer, Target
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import RelatedTools from "@/components/RelatedTools";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function AdvancedSEOTools() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("site-audit");
  const [loading, setLoading] = useState(false);

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Site Audit State
  const [auditUrl, setAuditUrl] = useState("");
  const [auditResult, setAuditResult] = useState(null);
  const [maxPages, setMaxPages] = useState(30);

  // Sitemap State
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [sitemapResult, setSitemapResult] = useState(null);

  // Schema State
  const [schemaType, setSchemaType] = useState("Article");
  const [schemaData, setSchemaData] = useState({});
  const [schemaResult, setSchemaResult] = useState(null);
  const [schemaTypes, setSchemaTypes] = useState([]);

  // Keyword Gap State
  const [yourDomain, setYourDomain] = useState("");
  const [competitors, setCompetitors] = useState("");
  const [gapResult, setGapResult] = useState(null);

  // GSC State
  const [gscSites, setGscSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [gscData, setGscData] = useState(null);

  const tools = [
    { id: "site-audit", name: "Site Audit", icon: <FileSearch className="w-5 h-5" />, desc: "SEMrush-style audit" },
    { id: "sitemap", name: "Sitemap Generator", icon: <Globe className="w-5 h-5" />, desc: "Crawl & generate sitemap" },
    { id: "schema", name: "Schema Generator", icon: <Code className="w-5 h-5" />, desc: "JSON-LD with preview" },
    { id: "keyword-gap", name: "Keyword Gap", icon: <BarChart3 className="w-5 h-5" />, desc: "Competitor analysis" },
    { id: "rankings", name: "Real Rankings", icon: <TrendingUp className="w-5 h-5" />, desc: "Google Search Console" },
  ];

  useEffect(() => {
    fetchSchemaTypes();
    fetchGSCSites();
  }, []);

  const fetchSchemaTypes = async () => {
    try {
      const res = await axios.get(`${API}/advanced-seo/schema/types`, { headers: { 'X-Skip-Auth-Error': 'true' } });
      setSchemaTypes(res.data.types || []);
    } catch (e) { console.error(e); }
  };

  const fetchGSCSites = async () => {
    try {
      const res = await axios.get(`${API}/gsc/sites`, { headers: { 'X-Skip-Auth-Error': 'true' } });
      setGscSites(res.data.sites || []);
      if (res.data.sites?.length) setSelectedSite(res.data.sites[0].site_url);
    } catch (e) { console.error(e); }
  };

  const runSiteAudit = async () => {
    if (!auditUrl) return toast.error("Enter website URL");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advanced-seo/site-audit`, {
        url: auditUrl, max_pages: maxPages
      });
      setAuditResult(res.data);
      toast.success(`Audited ${res.data.summary.pages_crawled} pages`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    }
    setLoading(false);
  };

  const generateSitemap = async () => {
    if (!sitemapUrl) return toast.error("Enter website URL");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advanced-seo/sitemap/crawl`, {
        url: sitemapUrl, max_pages: 100
      });
      setSitemapResult(res.data);
      toast.success(`Found ${res.data.pages_found} pages`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crawl failed");
    }
    setLoading(false);
  };

  const generateSchema = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advanced-seo/schema/generate`, {
        schema_type: schemaType, data: schemaData
      });
      setSchemaResult(res.data);
      toast.success("Schema generated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    }
    setLoading(false);
  };

  const analyzeKeywordGap = async () => {
    if (!yourDomain || !competitors) return toast.error("Enter domains");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/advanced-seo/keyword-gap`, {
        your_domain: yourDomain,
        competitor_domains: competitors.split(',').map(c => c.trim())
      });
      setGapResult(res.data);
      toast.success("Analysis complete!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    }
    setLoading(false);
  };

  const fetchGSCData = async () => {
    if (!selectedSite) return toast.error("Select a site");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gsc/top-queries`, {
        params: { site_url: selectedSite, days: 28, limit: 50 }
      });
      setGscData(res.data);
      toast.success("Rankings loaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/tools/seo")}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold">Advanced SEO Tools</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <Card className="p-4 h-fit lg:sticky lg:top-24">
            <h3 className="font-semibold mb-4">Tools</h3>
            <div className="space-y-2">
              {tools.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => setActiveTool(tool.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition ${
                    activeTool === tool.id ? 'bg-violet-100 text-violet-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {tool.icon}
                    <div>
                      <p className="font-medium text-sm">{tool.name}</p>
                      <p className="text-xs text-gray-500">{tool.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Site Audit */}
            {activeTool === "site-audit" && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-2">SEMrush-Style Site Audit</h2>
                <p className="text-gray-600 mb-6">Comprehensive analysis of your entire website</p>
                
                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="https://example.com"
                    value={auditUrl}
                    onChange={e => setAuditUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Select value={String(maxPages)} onValueChange={v => setMaxPages(Number(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 pages</SelectItem>
                      <SelectItem value="30">30 pages</SelectItem>
                      <SelectItem value="50">50 pages</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={runSiteAudit} disabled={loading} className="bg-violet-600 hover:bg-violet-700">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Run Audit"}
                  </Button>
                </div>

                {auditResult && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-100 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Health Score</p>
                        <p className={`text-3xl font-bold ${getScoreColor(auditResult.summary.health_score)}`}>
                          {auditResult.summary.health_score}%
                        </p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Errors</p>
                        <p className="text-3xl font-bold text-red-600">{auditResult.summary.errors}</p>
                      </div>
                      <div className="p-4 bg-yellow-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Warnings</p>
                        <p className="text-3xl font-bold text-yellow-600">{auditResult.summary.warnings}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Pages Crawled</p>
                        <p className="text-3xl font-bold text-blue-600">{auditResult.summary.pages_crawled}</p>
                      </div>
                    </div>

                    {/* Top Issues */}
                    <div>
                      <h3 className="font-semibold mb-3">Top Issues</h3>
                      <div className="space-y-2">
                        {auditResult.top_issues?.map((issue, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                            <div className="flex items-center gap-3">
                              {issue.severity === 'high' ? 
                                <XCircle className="w-5 h-5 text-red-500" /> : 
                                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                              }
                              <div>
                                <p className="font-medium">{issue.category}</p>
                                <p className="text-sm text-gray-500">{issue.sample}</p>
                              </div>
                            </div>
                            <Badge variant="secondary">{issue.count} pages</Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pages List */}
                    <div>
                      <h3 className="font-semibold mb-3">Page Analysis</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left">URL</th>
                              <th className="px-4 py-2 text-center">Score</th>
                              <th className="px-4 py-2 text-center">Issues</th>
                              <th className="px-4 py-2 text-center">Load Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {auditResult.pages?.slice(0, 10).map((page, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-4 py-2 max-w-xs truncate">{page.url}</td>
                                <td className={`px-4 py-2 text-center font-bold ${getScoreColor(page.score)}`}>{page.score}</td>
                                <td className="px-4 py-2 text-center">{page.issues?.length || 0}</td>
                                <td className="px-4 py-2 text-center">{page.load_time?.toFixed(2)}s</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Sitemap Generator */}
            {activeTool === "sitemap" && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-2">Sitemap Generator</h2>
                <p className="text-gray-600 mb-6">Crawl your website and generate XML sitemap</p>
                
                <div className="flex gap-4 mb-6">
                  <Input
                    placeholder="https://example.com"
                    value={sitemapUrl}
                    onChange={e => setSitemapUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={generateSitemap} disabled={loading} className="bg-green-600 hover:bg-green-700">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Crawl & Generate"}
                  </Button>
                </div>

                {sitemapResult && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-green-800">Sitemap Generated!</p>
                        <p className="text-sm text-green-600">{sitemapResult.pages_found} pages found</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(sitemapResult.sitemap_xml)}>
                          <Copy className="w-4 h-4 mr-1" /> Copy
                        </Button>
                        <Button size="sm" onClick={() => downloadFile(sitemapResult.sitemap_xml, sitemapResult.download_filename)}>
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto max-h-96">
                      <pre className="text-xs">{sitemapResult.sitemap_xml}</pre>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Schema Generator */}
            {activeTool === "schema" && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-2">Schema Generator</h2>
                <p className="text-gray-600 mb-6">Generate JSON-LD structured data with live preview</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Schema Type</label>
                    <Select value={schemaType} onValueChange={setSchemaType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {schemaTypes.map(t => (
                          <SelectItem key={t.type} value={t.type}>{t.type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="mt-4 space-y-3">
                      {schemaType === "Article" && (
                        <>
                          <Input placeholder="Headline" onChange={e => setSchemaData({...schemaData, headline: e.target.value})} />
                          <Textarea placeholder="Description" onChange={e => setSchemaData({...schemaData, description: e.target.value})} />
                          <Input placeholder="Author Name" onChange={e => setSchemaData({...schemaData, author: e.target.value})} />
                          <Input placeholder="Image URL" onChange={e => setSchemaData({...schemaData, image: e.target.value})} />
                          <Input placeholder="Publisher" onChange={e => setSchemaData({...schemaData, publisher: e.target.value})} />
                        </>
                      )}
                      {schemaType === "Product" && (
                        <>
                          <Input placeholder="Product Name" onChange={e => setSchemaData({...schemaData, name: e.target.value})} />
                          <Textarea placeholder="Description" onChange={e => setSchemaData({...schemaData, description: e.target.value})} />
                          <Input placeholder="Price" type="number" onChange={e => setSchemaData({...schemaData, price: e.target.value})} />
                          <Input placeholder="Brand" onChange={e => setSchemaData({...schemaData, brand: e.target.value})} />
                          <Input placeholder="Image URL" onChange={e => setSchemaData({...schemaData, image: e.target.value})} />
                        </>
                      )}
                      {schemaType === "LocalBusiness" && (
                        <>
                          <Input placeholder="Business Name" onChange={e => setSchemaData({...schemaData, name: e.target.value})} />
                          <Input placeholder="Phone" onChange={e => setSchemaData({...schemaData, phone: e.target.value})} />
                          <Input placeholder="Street Address" onChange={e => setSchemaData({...schemaData, streetAddress: e.target.value})} />
                          <Input placeholder="City" onChange={e => setSchemaData({...schemaData, city: e.target.value})} />
                          <Input placeholder="State" onChange={e => setSchemaData({...schemaData, state: e.target.value})} />
                          <Input placeholder="Postal Code" onChange={e => setSchemaData({...schemaData, postalCode: e.target.value})} />
                        </>
                      )}
                      {schemaType === "FAQ" && (
                        <Textarea 
                          placeholder="Enter questions (one per line)&#10;Q: Question 1?&#10;A: Answer 1&#10;Q: Question 2?&#10;A: Answer 2"
                          rows={6}
                          onChange={e => {
                            const lines = e.target.value.split('\n');
                            const questions = [];
                            let currentQ = null;
                            lines.forEach(line => {
                              if (line.startsWith('Q:')) currentQ = { question: line.slice(2).trim() };
                              else if (line.startsWith('A:') && currentQ) {
                                currentQ.answer = line.slice(2).trim();
                                questions.push(currentQ);
                                currentQ = null;
                              }
                            });
                            setSchemaData({...schemaData, questions});
                          }}
                        />
                      )}
                    </div>

                    <Button onClick={generateSchema} disabled={loading} className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Generate Schema"}
                    </Button>
                  </div>

                  <div>
                    {schemaResult && (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold">Generated Schema</h3>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(schemaResult.script_tag)}>
                            <Copy className="w-4 h-4 mr-1" /> Copy
                          </Button>
                        </div>
                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-80">
                          <pre className="text-xs">{JSON.stringify(schemaResult.json_ld, null, 2)}</pre>
                        </div>
                        <a 
                          href={schemaResult.validation_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block text-center text-blue-600 hover:underline text-sm"
                        >
                          Test in Google Rich Results →
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Keyword Gap */}
            {activeTool === "keyword-gap" && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-2">Keyword Gap Analysis</h2>
                <p className="text-gray-600 mb-6">Find keywords your competitors rank for</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Your Domain</label>
                    <Input placeholder="yourdomain.com" value={yourDomain} onChange={e => setYourDomain(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Competitor Domains (comma separated)</label>
                    <Input placeholder="competitor1.com, competitor2.com" value={competitors} onChange={e => setCompetitors(e.target.value)} />
                  </div>
                </div>
                
                <Button onClick={analyzeKeywordGap} disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Analyze Gap"}
                </Button>

                {gapResult && (
                  <div className="mt-6 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Keywords Analyzed</p>
                        <p className="text-2xl font-bold text-blue-600">{gapResult.summary.total_keywords_analyzed}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Gap Keywords</p>
                        <p className="text-2xl font-bold text-red-600">{gapResult.summary.gap_keywords}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Common Keywords</p>
                        <p className="text-2xl font-bold text-green-600">{gapResult.summary.common_keywords}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Potential Traffic</p>
                        <p className="text-2xl font-bold text-purple-600">{gapResult.summary.potential_traffic?.toLocaleString()}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold mb-3">Gap Keywords (Competitors have, you don't)</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left">Keyword</th>
                              <th className="px-4 py-2 text-right">Volume</th>
                              <th className="px-4 py-2 text-right">Difficulty</th>
                              <th className="px-4 py-2 text-center">Your Rank</th>
                            </tr>
                          </thead>
                          <tbody>
                            {gapResult.gap_keywords?.slice(0, 10).map((kw, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-4 py-2 font-medium">{kw.keyword}</td>
                                <td className="px-4 py-2 text-right">{kw.search_volume?.toLocaleString()}</td>
                                <td className="px-4 py-2 text-right">
                                  <Badge variant={kw.difficulty < 40 ? "success" : kw.difficulty < 60 ? "warning" : "destructive"}>
                                    {kw.difficulty}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2 text-center text-red-500">Not Ranking</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* GSC Rankings */}
            {activeTool === "rankings" && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-2">Real Rankings (Google Search Console)</h2>
                <p className="text-gray-600 mb-6">Actual search performance data from Google</p>
                
                <div className="flex gap-4 mb-6">
                  <Select value={selectedSite} onValueChange={setSelectedSite}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select verified site" />
                    </SelectTrigger>
                    <SelectContent>
                      {gscSites.map(site => (
                        <SelectItem key={site.site_url} value={site.site_url}>{site.site_url}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchGSCData} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Get Rankings"}
                  </Button>
                </div>

                {gscData?.source === 'demo_data' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 mb-4">
                    Demo data shown. Connect your Google Search Console for real rankings.
                  </div>
                )}

                {gscData && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Query</th>
                          <th className="px-4 py-2 text-right"><Eye className="w-4 h-4 inline" /> Impressions</th>
                          <th className="px-4 py-2 text-right"><MousePointer className="w-4 h-4 inline" /> Clicks</th>
                          <th className="px-4 py-2 text-right">CTR</th>
                          <th className="px-4 py-2 text-right"><Target className="w-4 h-4 inline" /> Position</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gscData.rows?.map((row, idx) => (
                          <tr key={idx} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">{row.keys?.[0]}</td>
                            <td className="px-4 py-2 text-right">{row.impressions?.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">{row.clicks?.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">{row.ctr}%</td>
                            <td className="px-4 py-2 text-right">
                              <Badge variant={row.position <= 10 ? "success" : row.position <= 30 ? "warning" : "secondary"}>
                                #{row.position}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="seo" />
        </div>
      </div>
    </div>
  );
}
